import React, { useState } from 'react';
import { BASE_URL } from "../backend/backend.js";

const DataFileUploader = () => {
  const [file, setFile] = useState(null);
  
  // CSV Preview States
  const [csvText, setCsvText] = useState("");
  const [showCsvPreview, setShowCsvPreview] = useState(false);
  
  // JSON Conversion States
  const [jsonData, setJsonData] = useState(null);
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState(null);
  
  // Database Upload States
  const [dbReport, setDbReport] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle file selection
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    
    // Reset all states on new file selection
    setJsonData(null);
    setDbReport(null);
    setDownloadUrl(null);
    setTotalRows(0);
    setShowCsvPreview(false);
    setShowJsonPreview(false);

    if (selectedFile && selectedFile.type === "text/csv") {
      setFile(selectedFile);
      setError(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        setCsvText(e.target.result);
      };
      reader.readAsText(selectedFile.slice(0, 100000)); 
      
    } else {
      setFile(null);
      setCsvText("");
      setError("Please select a valid CSV file.");
    }
  };

  // 🚀 ACTION 1: Convert to JSON & Download
  const handleConvertToJSON = async () => {
    if (!file) return setError("No file selected.");

    setLoading(true);
    setError(null);
    setDbReport(null); // Clear DB report if switching actions
    setShowCsvPreview(false); 

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${BASE_URL}/api/files/csv-to-json-stream`, {
        method: 'POST',
        body: formData,
        credentials: "include", 
      });

      if (response.status === 401 || response.type === 'opaqueredirect' || response.status === 302) {
         throw new Error("Session expired or unauthorized. Please log in again.");
      }
      if (!response.ok) throw new Error("Upload failed. Check server logs.");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setDownloadUrl(url);

      const textData = await blob.text();
      const parsedJson = JSON.parse(textData);
      
      setTotalRows(parsedJson.length);
      setJsonData(parsedJson);

    } catch (err) {
      setError(err.message || "Error processing the file.");
    } finally {
      setLoading(false);
    }
  };

  // 🚀 ACTION 2: Upload directly to Database
  const handleUploadToDatabase = async () => {
    if (!file) return setError("No file selected.");

    setLoading(true);
    setError(null);
    setJsonData(null); // Clear JSON data if switching actions
    setShowCsvPreview(false);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${BASE_URL}/api/files/upload-medicine-csv`, {
        method: 'POST',
        body: formData,
        credentials: "include", 
      });

      if (response.status === 401 || response.type === 'opaqueredirect' || response.status === 302) {
         throw new Error("Session expired or unauthorized. Please log in again.");
      }
      if (!response.ok) throw new Error("Database upload failed. Check server logs.");

      // Parse the report returned by our new Spring Boot endpoint
      const data = await response.json();
      setDbReport(data);

    } catch (err) {
      setError(err.message || "Error saving to database.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>CSV Data Manager</h2>
      
      <div style={styles.uploadSection}>
        <input 
          type="file" 
          accept=".csv" 
          onChange={handleFileChange} 
          style={styles.fileInput}
        />
      </div>

      {/* --- Action Buttons --- */}
      <div style={styles.actionSection}>
        <button 
          onClick={handleConvertToJSON} 
          disabled={!file || loading}
          style={file && !loading ? styles.primaryButton : styles.buttonDisabled}
        >
          {loading ? 'Processing...' : '📄 Convert to JSON Only'}
        </button>

        <button 
          onClick={handleUploadToDatabase} 
          disabled={!file || loading}
          style={file && !loading ? styles.dbButton : styles.buttonDisabled}
        >
          {loading ? 'Processing...' : '💾 Save to Database'}
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* --- PRE-UPLOAD: CSV Preview Toggle --- */}
      {file && !loading && !jsonData && !dbReport && (
        <div style={styles.toggleContainer}>
          <button 
            onClick={() => setShowCsvPreview(!showCsvPreview)} 
            style={styles.previewButton}
          >
            {showCsvPreview ? "👁️ Hide CSV Preview" : "👁️ View CSV Preview"}
          </button>
          
          {showCsvPreview && (
            <div style={styles.resultSection}>
              <p style={styles.warningText}>Showing preview of raw file data...</p>
              <pre style={styles.dataOutput}>{csvText}</pre>
            </div>
          )}
        </div>
      )}

      {/* --- SCENARIO 1: JSON Result Section --- */}
      {jsonData && (
        <div style={styles.resultSection}>
          <div style={styles.successHeader}>
            <div>
              <h3 style={{ margin: 0, color: '#27ae60' }}>Conversion Successful!</h3>
              <p style={{ margin: '5px 0 0 0', color: '#555', fontSize: '14px' }}>
                Total rows converted: <strong>{totalRows.toLocaleString()}</strong>
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowJsonPreview(!showJsonPreview)} style={styles.previewButton}>
                {showJsonPreview ? "Hide JSON Preview" : "Preview JSON"}
              </button>
              {downloadUrl && (
                <a href={downloadUrl} download={`${file?.name.replace('.csv', '')}_converted.json`} style={{ textDecoration: 'none' }}>
                  <button style={styles.downloadButton}>⬇️ Download Full JSON</button>
                </a>
              )}
            </div>
          </div>
          {showJsonPreview && (
            <div style={{ marginTop: '15px' }}>
              {totalRows > 1000 && (
                 <p style={styles.warningText}>⚠️ Displaying first 1,000 rows to prevent browser freezing.</p>
              )}
              <pre style={styles.dataOutput}>{JSON.stringify(jsonData.slice(0, 1000), null, 2)}</pre>
            </div>
          )}
        </div>
      )}

      {/* --- SCENARIO 2: Database Upload Report Section --- */}
      {dbReport && (
        <div style={styles.resultSection}>
          <div style={styles.successHeader}>
            <div>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>Database Upload Report</h3>
              <p style={{ margin: '5px 0 0 0', color: '#555', fontSize: '14px' }}>
                {dbReport.message}
              </p>
            </div>
          </div>
          
          <div style={styles.statsContainer}>
             <div style={styles.statBoxSuccess}>
                <h4 style={{ margin: 0 }}>Successful Inserts</h4>
                <p style={{ fontSize: '24px', margin: '10px 0 0 0', fontWeight: 'bold' }}>{dbReport.successful_inserts}</p>
             </div>
             <div style={styles.statBoxFailed}>
                <h4 style={{ margin: 0 }}>Failed Inserts</h4>
                <p style={{ fontSize: '24px', margin: '10px 0 0 0', fontWeight: 'bold' }}>{dbReport.failed_inserts}</p>
             </div>
          </div>

          {dbReport.failed_inserts > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h4 style={{ color: '#c0392b' }}>Error Details:</h4>
              <pre style={styles.errorOutput}>
                {dbReport.error_details.map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Styles ---
const styles = {
  container: { maxWidth: '800px', margin: '40px auto', padding: '25px', fontFamily: 'Arial, sans-serif', boxShadow: '0 8px 16px rgba(0,0,0,0.1)', borderRadius: '12px', backgroundColor: '#fff' },
  heading: { textAlign: 'center', color: '#333', marginBottom: '20px' },
  uploadSection: { display: 'flex', justifyContent: 'center', marginBottom: '15px' },
  actionSection: { display: 'flex', gap: '15px', justifyContent: 'center', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '20px' },
  fileInput: { padding: '10px', border: '1px solid #ccc', borderRadius: '6px', width: '100%', maxWidth: '400px' },
  primaryButton: { padding: '10px 20px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', transition: '0.2s', flex: 1, maxWidth: '220px' },
  dbButton: { padding: '10px 20px', backgroundColor: '#8e44ad', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', transition: '0.2s', flex: 1, maxWidth: '220px' },
  buttonDisabled: { padding: '10px 20px', backgroundColor: '#ecf0f1', color: '#95a5a6', border: 'none', borderRadius: '6px', cursor: 'not-allowed', flex: 1, maxWidth: '220px' },
  downloadButton: { padding: '10px 15px', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
  previewButton: { padding: '10px 15px', backgroundColor: '#f39c12', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
  error: { color: '#e74c3c', backgroundColor: '#fadbd8', padding: '10px', borderRadius: '6px', textAlign: 'center', marginBottom: '20px' },
  toggleContainer: { marginTop: '10px', textAlign: 'center' },
  resultSection: { marginTop: '15px', paddingTop: '20px' },
  successHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' },
  warningText: { color: '#d35400', fontSize: '13px', fontWeight: 'bold', marginBottom: '10px', backgroundColor: '#fcf3cf', padding: '8px', borderRadius: '4px' },
  dataOutput: { backgroundColor: '#2c3e50', color: '#ecf0f1', padding: '15px', borderRadius: '8px', overflowX: 'auto', fontSize: '14px', lineHeight: '1.5', maxHeight: '400px', overflowY: 'auto' },
  errorOutput: { backgroundColor: '#fdf2e9', color: '#c0392b', padding: '15px', borderRadius: '8px', overflowX: 'auto', fontSize: '13px', maxHeight: '300px', overflowY: 'auto', border: '1px solid #fad7a1' },
  statsContainer: { display: 'flex', gap: '15px', marginTop: '10px' },
  statBoxSuccess: { flex: 1, backgroundColor: '#eafaf1', padding: '20px', borderRadius: '8px', textAlign: 'center', border: '1px solid #27ae60', color: '#27ae60' },
  statBoxFailed: { flex: 1, backgroundColor: '#fdedec', padding: '20px', borderRadius: '8px', textAlign: 'center', border: '1px solid #e74c3c', color: '#e74c3c' },
};

export default DataFileUploader;